import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import {
  Button,
  Icon,
  Input,
  Picker,
  Dialog,
} from '@tencent/spaui';
import { ListView } from '@tencent/ptp-fe-common';
import { RPCFactory } from '../../../utils/RequestUtil';
import { CorpSelector } from '../qw/corpSelector';

const EMPLOYEE_QUERY_SIZE = 10;

/**
 * 检查是否有某个操作的具体权限
 */
const valid = (str) => {
  const rights = _.get(window, 'pageProps.privileges');
  if (!rights) return true;
  return rights.indexOf(str) !== -1 || window.pageProps.originRole === 'OPERATOR_ROLE_LEADS_OBSERVER';
};

const UserItemComponent = (props = {}) => {
  const { type = '', status = 'add', onClick, disabled, data = {} } = props;
  if (type === 'user-list') {
    // 左侧的选择结构
    return (
      <li
        className={classNames('feed-item spaui-userpicker-feed-item', {
          checked: status === 'checked',
        })}
        onClick={onClick}
      >
        <div className="feed-info spaui-userpicker-feed-info">
          <span className="feed-title spaui-userpicker-feed-title">
            <ww-open-data type="userName" openid={data.wxWorkUserId} />
          </span>
          <p className="feed-desc spaui-userpicker-feed-desc">{data.describe}</p>
        </div>

        <span className="feed-action spaui-userpicker-feed-action">
          {status === 'checked' ? <i className="icon icon-checked"></i> : <i className="icon icon-plus"></i>}
        </span>
      </li>
    );
  }
  if (type === 'selection-list') {
    // 右侧的选中结构
    return (
      <li className="feed-item spaui-userpicker-feed-item feed-item-delete spaui-userpicker-feed-item-delete">
        <div className="feed-info spaui-userpicker-feed-info">
          <span className="feed-title spaui-userpicker-feed-title">
            <ww-open-data type="userName" openid={data.wxWorkUserId} />
          </span>
        </div>
        <span className="feed-action spaui-userpicker-feed-action">
          <i className="icon icon-plus icon-close" onClick={onClick}></i>
        </span>
      </li>
    );
  }
  return null;
};

// 编辑团队的弹窗
const EditTeamDialog = (props) => {
  if (props.currentDialogType !== 'team') return null;
  const isEdit = !!props.dialogData.isEdit;
  const data = { ...props.dialogData };
  delete data.isEdit;
  const [name, setName] = useState(data.team);
  const [members, setMembers] = useState(data.members || []);
  const [memberList, setMemberList] = useState([]);
  const [memberSearchs, setMemberSearchs] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const getMemberList = useCallback((obj) => new Promise((res, rej) => {
    const params = obj || {};
    let index = params.index || 1;
    if (params.name) {
      index = 1;
    }
    RPCFactory.qwEmployee.getList({
      pageSize: EMPLOYEE_QUERY_SIZE,
      pageIndex: index,
      name: params.name,
    }).done((data) => {
      console.log('员工', data)
      const list = data.entities || [];
      res(list);
    }).fail((ret, msg) => {
      rej(msg);
      window.alert(msg);
    });
  }), []);

  const handleMembersChange = (e, value) => {
    setMembers(value);
  };

  const handleSearchMember = (e, value) => {
    setSearchValue(value);
    if (typeof value === 'string' && value.length > 0) {
      getMemberList({ name: value }).then(setSearchValue);
    } else {
      setMemberSearchs([]);
    }
  };

  const handleLoadMoreMembers = () => {
    const index = 1 + Math.floor(memberList.length / EMPLOYEE_QUERY_SIZE);
    getMemberList({ index }).then((arr) => {
      return _.uniqBy(memberList.concat(arr), 'wxWorkUserId');
    }).then(setMemberList);
  };

  const handleCloseDialog = () => {
    if (typeof props.onDialogCancel !== 'function') return;
    props.onDialogCancel();
  };

  const handleSubmit = () => {
    // TODO: 发送修改请求，完成修改后刷新列表，最后调用关闭弹窗的方法
    if (name.length < 1) {
      window.alert('请填写团队名称');
      return;
    }
    if (members.length < 1) {
      window.alert('请选择团队成员');
      return;
    }
    const submitFn = data.groupId ? RPCFactory.qwTeam.editTeam : RPCFactory.qwTeam.addTeam;
    submitFn({
      groupId: data.groupId,
      groupName: name,
      wxWorkUserIds: members.map((item) => item.wxWorkUserId),
    }).done(() => {
      if (typeof props.onDialogSubmit === 'function') {
        props.onDialogSubmit();
      }
      handleCloseDialog();
    }).fail((ret, msg) => {
      window.alert(msg);
    });
  };

  useEffect(() => {
    getMemberList().then(setMemberList);
  }, [getMemberList]);

  useEffect(() => {
    const id = props.dialogData.groupId;
    if (!id) return;
    RPCFactory.qwTeam.teamDetail(id)
      .done((data) => {
        console.log('团队详情', data);
        setMembers(data.wxWorkUsers || []);
      })
      .fail((ret, msg) => window.alert(msg));
  }, [props.dialogData]);

  const pickerValue = useMemo(() => {
    if (searchValue.length < 1) return memberList;
    // 查找的时候注意如果查找的已存在的目标需要对合并数组进行排重
    return _.uniqBy(memberList.concat(searchValue), 'wxWorkUserId');
  }, [searchValue, memberList, memberSearchs]);

  return (
    <Dialog
      show
      type="simple"
      title={`${isEdit ? '编辑' : '添加'}团队`}
      style={{ width: 750 }}
      onSubmit={handleSubmit}
      onCancel={handleCloseDialog}
      onClose={handleCloseDialog}
    >
      <div>
        <br />
        <p style={{ marginBottom: 5 }}>团队名称</p>
        <Input
          placeholder="请填写团队名称"
          onChange={(e, value) => setName(value)}
          value={name}
        />
        <br />
        <p style={{ marginBottom: 5 }}>选择队员</p>
        <Picker
          UserItemComponent={UserItemComponent}
          selectionTitle="已选择员工"
          selectionEmptyLabel="请在左侧选择添加员工"
          valueKey="wxWorkUserId"
          searchPlaceholder="输入员工姓名进行搜索"
          asyncLoading={!searchValue}
          valueType="object"
          value={members}
          data={pickerValue}
          onChange={handleMembersChange}
          onSearchChange={handleSearchMember}
          onLoad={handleLoadMoreMembers}
          search={false}
        />
      </div>
    </Dialog>
  );
};

// 删除团队的弹窗
const DeleteTeamDialog = (props) => {
  if (props.currentDialogType !== 'deleteteam') return null;

  const data = { ...props.dialogData };

  const handleCloseDialog = () => {
    if (typeof props.onDialogCancel !== 'function') return;
    props.onDialogCancel();
  };

  const handleSubmit = () => {
    let submitFn = () => {};
    if (typeof props.onDialogSubmit === 'function') {
      submitFn = props.onDialogSubmit;
    }
    RPCFactory.qwTeam.deleteDetail(data.groupId)
      .done(() => {
        submitFn();
        handleCloseDialog();
      })
      .fail((ret, msg) => window.alert(msg));
  };

  useEffect(() => {
    console.log('delete', props);
  }, [props]);

  return (
    <Dialog
      show
      type="normal"
      iconType="tip"
      title="确认是否删除团队"
      style={{ width: 400, textAlign: 'center' }}
      footerStyle={{ textAlign: 'center' }}
      onSubmit={handleSubmit}
      onCancel={handleCloseDialog}
      onClose={handleCloseDialog}
    ></Dialog>
  );
};

const List = (props) => {
  const lvRef = useRef();
  const [dialog, setDialog] = useState(null);
  const [dialogData, setDialogData] = useState({});

  // 展示的列
  const showColumns = [
    'team',
    'employees',
    'options',
  ];

  const getList = (params) => new Promise((res, rej) => {
    RPCFactory.qwTeam.teamList(params)
      .done((data) => {
        const pageData = {
          list: [],
          page_info: { total_number: 0 },
        };

        pageData.list = data.entities.map(item => ({
          team: item.groupName,
          employees: item.userNum,
          groupId: item.groupId,
        }));
        pageData.page_info.total_number = data.size;
        res(pageData);
      })
      .fail((ret, msg) => {
        rej(msg);
        window.alert(msg);
      });
  });

  const reloadList = () => {
    if (typeof lvRef.current.loadList !== 'function') return;
    lvRef.current.loadList();
  };

  const handleOpenDialog = (type, data) => {
    setDialog(type);
    setDialogData(data);
  };

  const handleOpenEditDialog = (data, isCreate) => {
    handleOpenDialog('team', { ...data, isEdit: !isCreate });
  };

  // ----------------------- 列渲染方法 -----------------------
  // 操作
  const renderOptions = (type, name, value, _data) => {
    const openEditDialog = (e) => {
      e.preventDefault();
      handleOpenEditDialog(_data, false);
    };

    const openDeletDialog = (e) => {
      e.preventDefault();
      handleOpenDialog('deleteteam', _data);
    };

    return (
      <div>
        {
          valid('WX_WORK_UPDATEGROUP') && (
            <a href="#" onClick={openEditDialog}>编辑</a>
          )
        }
        {
          valid('WX_WORK_DELETEGROUP') && (
            <a href="#" onClick={openDeletDialog}>删除</a>
          )
        }
      </div>
    );
  };
  // --------------------------------------------------------

  // 列渲染方法 map
  // 注意：使用的时候需要注入 name: [key] 属性
  const columnMap = {
    team: { title: '团队名称' },
    employees: { title: '员工人数' },
    options: { title: '操作', width: 120, render: renderOptions },
  };

  // TODO: 跟需求的筛选条件全部得对上
  const filterControls = [
    {
      name: 'team',
      title: '团队名称',
      type: 'search',
      rules: [{
        message: '请输入姓名',
        function(str) {
          if (str.length < 1) return true;
          return /^[\u4e00-\u9fa5_a-zA-Z0-9]+$/.test(str);
        },
      }],
    },
  ];

  const handleCloseDialog = () => {
    setDialogData({});
    setDialog(null);
  };

  const lvOptions = () => ({
    fetchConfig: {
      action: getList,
      $onRequest(params) {
        const obj = {
          pageIndex: params.page,
          pageSize: params.page_size,
          groupName: params.team,
        };
        return obj;
      },
      $onResponse(data) {
        // TODO: 在这里处理返回的数据的格式
        return data;
      },
      $onError: window.alert,
    },
    toolbarConfig: {
      // left: (
      //   <div className="toolbar-left">
      //     <h4>团队列表</h4>
      //   </div>
      // ),
      left: (
        <div className="toolbar-right-group">
          {
            valid('WX_WORK_ADDGROUP') && (
              <Button
                displayType="primary"
                onClick={() => handleOpenEditDialog({}, true)}
              >
                <Icon type="add" />
                新增团队
              </Button>
            )
          }
        </div>
      ),
    },
    filterConfig: {
      resetable: false,
      controls: filterControls,
      customConfigure: null,
      defaultValuesByKey: {},
    },
    batchConfig: null,
    tableConfig: {
      defaultPageSize: 10,
      select: null,
      columns: showColumns.map(key => {
        return { ...columnMap[key], name: key };
      }),
      optionalConfig: {},
    },
  });

  return (
    <main id="main" className="main">
      <CorpSelector {...props} onChange={reloadList} />
      <div className="page-pool">
        <div className="card borderd" style={{ paddingTop: 0 }}>
          <div className="datalist">
            <ListView
              className="lv"
              ref={lvRef}
              options={lvOptions}
              // payloads={this.payloads} // 不知道干啥用的
              // customFunctions={this.customFunctions} // 一样不知道干啥的
            />
          </div>
        </div>
      </div>
      <EditTeamDialog
        currentDialogType={dialog}
        dialogData={dialogData}
        onDialogCancel={handleCloseDialog}
        onDialogSubmit={reloadList}
      />
      <DeleteTeamDialog
        currentDialogType={dialog}
        dialogData={dialogData}
        onDialogCancel={handleCloseDialog}
        onDialogSubmit={reloadList}
      />
    </main>
  );
};

export default List;
